import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  MenuItem,
  RadioGroup,
  Radio,
  FormLabel,
  FormControl,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import api from '../../api/api';
import en_ja from '../../translations/en_ja';

const AddDeepLink = ({
  open,
  onClose,
  editMode = false,
  deepLinkData,
  onSubmit,
  setSnackbarType,
  setSnackbarOpen,
  setSnackbarMessage,
}) => {

  const [loading, setLoading] = useState(false);

  const [deepLinkTypes, setDeepLinkTypes] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    link_kind: 'internal',
    type: '',
    external_url: '',
    status: 'active',
    description: '',
    ref_id: null,
  });

  
  const [errors, setErrors] = useState({});

  const getDeepLinkTypes = async () => {
    try {
      const res = await api.get('/v1/admin/deep-link-types');

      if (res?.data?.data) {
        setDeepLinkTypes(res.data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    getDeepLinkTypes();
  }, []);

  useEffect(() => {
    if (editMode && deepLinkData) {
      setFormData({
        name: deepLinkData.name || '',
        link_kind: deepLinkData.link_kind || 'internal',
        type:
          deepLinkData.type
            ?.toLowerCase()
            ?.replaceAll(' ', '_') || '',
        external_url: deepLinkData.external_url || '',
        status: deepLinkData.status || 'active',
        description:
          deepLinkData.description || '',
        ref_id: deepLinkData.ref_id ?? null,
      });
    }
  }, [editMode, deepLinkData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: '',
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name =
        en_ja['Deep Link Name is required']
    }

    if (formData.link_kind === 'internal') {
      if (!formData.type) {
        newErrors.type =
          en_ja['Destination Type is required']
      }
    } else {
      if (!formData.external_url.trim()) {
        newErrors.external_url =
          en_ja['External Link is required']
      } else if (
        !/^https?:\/\/.+/i.test(formData.external_url.trim())
      ) {
        newErrors.external_url =
          en_ja['External Link must start with http:// or https://']
      }
    }

    if (!formData.status) {
      newErrors.status =
        en_ja['Status is required']
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);

    let payload;

    if (editMode) {
      payload = {
        id: deepLinkData?.id,
        name: formData.name,
        code: deepLinkData?.code,
        link_kind: formData.link_kind,
        type: formData.link_kind === 'internal' ? formData.type : deepLinkData?.type,
        external_url: formData.link_kind === 'external' ? formData.external_url : deepLinkData?.external_url,
        ref_id: formData.ref_id ?? deepLinkData?.ref_id ?? null,
        status: formData.status,
        description: formData.description,
        // deep_link_url: deepLinkData?.deep_link_url,
        // created_at: deepLinkData?.created_at,
        updated_at: deepLinkData?.updated_at,
      };
    } else {
      payload = {
        name: formData.name,
        link_kind: formData.link_kind,
        status: formData.status,
        description: formData.description,
        ...(formData.link_kind === 'internal'
          ? { type: formData.type }
          : { external_url: formData.external_url }),
      };
    }

    try {
      let response;

      if (editMode) {
        response = await api.put(
          `/v1/admin/deep-links/${deepLinkData.id}`,
          payload
        );
      } else {
        response = await api.post(
          '/v1/admin/deep-links',
          payload
        );
      }

      if (
        response?.status === 200 ||
        response?.status === 201
      ) {
        setSnackbarMessage(
          editMode
            ? en_ja['Deep link updated successfully']
            : en_ja['Deep link created successfully']
        );

        setSnackbarType('success');
        setSnackbarOpen(true);

        if (onSubmit) {
          onSubmit();
        }

        handleClose();
      }
    } catch (error) {
      console.error(error);

      setSnackbarMessage(
        error?.response?.data?.message ||
        'Something went wrong'
      );

      setSnackbarType('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      link_kind: 'internal',
      type: '',
      external_url: '',
      status: 'active',
      description: '',
      ref_id: null,
    });

    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom:
            '1px solid #E5E7EB',
          pb: 2,
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
            >
              {editMode
                ? en_ja['Edit Deep Link']
                : en_ja['Create New Deep Link']
              }
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              mt={0.5}
            >
              {en_ja["Fill in the details to generate a new deep link"]}
            </Typography>
          </Box>

          <IconButton
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>

        <FormControl sx={{ mt: 1, mb: 1 }}>
          <FormLabel>{en_ja["Link Type"]}</FormLabel>
          <RadioGroup
            row
            name="link_kind"
            value={formData.link_kind}
            onChange={handleChange}
          >
            <FormControlLabel
              value="internal"
              control={<Radio />}
              label={en_ja["Internal"]}
              disabled={editMode && formData.link_kind === 'external'}
            />
            <FormControlLabel
              value="external"
              control={<Radio />}
              label={en_ja["External"]}
              disabled={editMode && formData.link_kind === 'internal'}
            />
          </RadioGroup>
        </FormControl>

        <TextField
          fullWidth
          label={en_ja["Deep Link Name"]}
          name="name"
          placeholder={en_ja["Deep Link Name"]}
          value={formData.name}
          onChange={handleChange}
          margin="normal"
          error={!!errors.name}
          helperText={errors.name}
        />

        {formData.link_kind === 'internal' ? (
          <TextField
            key="destination-type-field"
            select
            fullWidth
            label={en_ja["Destination Type"]}
            name="type"
            placeholder={en_ja["Destination Type"]}
            value={formData.type}
            onChange={handleChange}
            margin="normal"
            error={!!errors.type}
            helperText={errors.type}
          >
            {deepLinkTypes.map((item) => (
              <MenuItem
                key={item.key}
                value={item.key}
              >
                {item.label}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            key="external-url-field"
            fullWidth
            label={en_ja["External Link"]}
            name="external_url"
            placeholder={en_ja["External Link"]}
            value={formData.external_url}
            onChange={handleChange}
            margin="normal"
            error={!!errors.external_url}
            helperText={errors.external_url}
          />
        )}

        <TextField
          select
          fullWidth
          label={en_ja["Status"]}
          name="status"
          value={formData.status}
          onChange={handleChange}
          margin="normal"
        >
          <MenuItem value="active">
            {en_ja["Active"]}
          </MenuItem>

          <MenuItem value="disabled">
            {en_ja["Disabled"]}
          </MenuItem>
        </TextField>

        <TextField
          fullWidth
          multiline
          rows={4}
          label={en_ja["Description"]}
          name="description"
          placeholder={en_ja["Description"]}
          value={formData.description}
          onChange={handleChange}
          margin="normal"
        />
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>

        <Button
          onClick={handleClose}
          variant="outlined"
        >
          {en_ja['Cancel']}
        </Button>

        <LoadingButton
          loading={loading}
          variant="contained"
          onClick={handleSubmit}
          sx={{ backgroundColor: "rgba(220, 53, 69, 1)" }}
        >
          {editMode
            ? en_ja['Update Deep Link']
            : en_ja['Create Deep Link']
          }
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddDeepLink;